import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Shield, X, ArrowRight, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY_PREFIX = 'caraloom_mfa_nudge_dismissed_v1_';
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * MfaNudgeBanner — HIPAA Readiness follow-through, Iter 139.
 *
 * Renders a slim banner above the dashboard content whenever the
 * current user is in an MFA-required role AND has not yet enabled it.
 * Severity is driven by backend rules:
 *   - critical (red)  → admin without MFA, or grace expired
 *   - warn     (amber)→ < 4 days of grace left
 *   - info     (blue) → informational nudge, grace still healthy
 *
 * Dismissing hides the banner for 24 hours (per-user, per-browser) so
 * users aren't nagged constantly — but the banner is force-shown
 * (dismiss ignored) when severity=critical.
 */
export default function MfaNudgeBanner() {
  const { user, getAuthHeader } = useAuth();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const dismissKey = user?.id ? `${DISMISS_KEY_PREFIX}${user.id}` : null;

  const load = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data } = await axios.get(
        `${API}/auth/mfa-nudge-status`,
        getAuthHeader(),
      );
      setStatus(data);
    } catch {
      // Silent — banner is a nudge, not a hard gate.
    }
  }, [user?.id, getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  // Read the local-storage dismiss stamp when the status resolves.
  useEffect(() => {
    if (!dismissKey) return;
    try {
      const raw = window.localStorage.getItem(dismissKey);
      if (!raw) return;
      const { ts } = JSON.parse(raw);
      if (Date.now() - ts < DISMISS_TTL_MS) setDismissed(true);
    } catch { /* ignore */ }
  }, [dismissKey]);

  if (!status || !status.should_nudge) return null;
  // Critical severity always shows — the user cannot dismiss a hard-gate warning.
  if (dismissed && status.severity !== 'critical') return null;

  const onDismiss = () => {
    if (!dismissKey) return;
    try {
      window.localStorage.setItem(dismissKey, JSON.stringify({ ts: Date.now() }));
    } catch { /* ignore quota errors */ }
    setDismissed(true);
  };

  const palette = {
    critical: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-900',
      icon: <AlertTriangle className="w-4 h-4 text-red-600" />,
      cta: 'bg-red-600 hover:bg-red-700 text-white',
    },
    warn: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      text: 'text-amber-900',
      icon: <Shield className="w-4 h-4 text-amber-600" />,
      cta: 'bg-amber-600 hover:bg-amber-700 text-white',
    },
    info: {
      bg: 'bg-sky-50',
      border: 'border-sky-200',
      text: 'text-sky-900',
      icon: <Shield className="w-4 h-4 text-sky-600" />,
      cta: 'bg-sky-600 hover:bg-sky-700 text-white',
    },
  }[status.severity] || { bg: 'bg-sky-50', border: 'border-sky-200', text: 'text-sky-900', icon: <Shield className="w-4 h-4" />, cta: 'bg-sky-600 text-white' };

  return (
    <div
      className={`w-full ${palette.bg} border-b ${palette.border} px-4 py-2.5`}
      data-testid={`mfa-nudge-banner-${status.severity}`}
    >
      <div className="max-w-7xl mx-auto flex items-center gap-3">
        <div className="flex-shrink-0">{palette.icon}</div>
        <div className={`flex-1 text-xs md:text-sm ${palette.text}`} data-testid="mfa-nudge-headline">
          {status.headline}
        </div>
        <a
          href={status.cta_href}
          className={`flex-shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium ${palette.cta} transition-colors`}
          data-testid="mfa-nudge-cta"
        >
          Enable 2FA <ArrowRight className="w-3 h-3" />
        </a>
        {status.severity !== 'critical' && (
          <button
            type="button"
            onClick={onDismiss}
            className={`flex-shrink-0 p-1 rounded hover:bg-black/5 ${palette.text}`}
            aria-label="Dismiss for 24 hours"
            data-testid="mfa-nudge-dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
